import { useEffect, useRef } from 'react';
import { Spinner } from './Spinner';

interface ConfirmDialogProps {
  open: boolean;
  title: string;
  message: string;
  confirmLabel?: string;
  cancelLabel?: string;
  destructive?: boolean;
  busy?: boolean;
  onConfirm: () => void;
  onCancel: () => void;
}

/**
 * Reusable confirm modal for irreversible actions.
 * Uses a backdrop + centered card; dismissible via Cancel button or Escape.
 */
export function ConfirmDialog({
  open,
  title,
  message,
  confirmLabel = 'Confirm',
  cancelLabel = 'Cancel',
  destructive = false,
  busy = false,
  onConfirm,
  onCancel,
}: ConfirmDialogProps) {
  const confirmRef = useRef<HTMLButtonElement | null>(null);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !busy) onCancel();
    };
    window.addEventListener('keydown', onKey);
    // Auto-focus the confirm button so keyboard users can hit Enter.
    confirmRef.current?.focus();
    return () => window.removeEventListener('keydown', onKey);
  }, [open, busy, onCancel]);

  if (!open) return null;

  const confirmClasses = destructive
    ? 'bg-status-attention text-white hover:brightness-95'
    : 'bg-ss-blue text-white hover:bg-ss-blue-dark';

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="confirm-dialog-title"
      className="fixed inset-0 z-50 flex items-center justify-center bg-ink-900/40 px-6 backdrop-blur-sm"
      onClick={() => {
        if (!busy) onCancel();
      }}
    >
      <div
        className="w-full max-w-sm rounded-ss-lg bg-white p-5 shadow-ss-md dark:bg-[#1E293B]"
        onClick={(e) => e.stopPropagation()}
      >
        <h2
          id="confirm-dialog-title"
          className="text-base font-bold text-ink-900 dark:text-[#E2E8F0]"
        >
          {title}
        </h2>
        <p className="mt-2 text-sm leading-relaxed text-ink-700 dark:text-[#94A3B8]">{message}</p>
        <div className="mt-5 flex justify-end gap-2">
          <button
            type="button"
            disabled={busy}
            onClick={onCancel}
            className="rounded-ss-sm border border-ink-200 bg-white px-4 py-2 text-sm font-semibold text-ink-700 hover:bg-ink-50 disabled:opacity-50 dark:border-[#334155] dark:bg-transparent dark:text-[#94A3B8] dark:hover:bg-[#0F172A]"
          >
            {cancelLabel}
          </button>
          <button
            ref={confirmRef}
            type="button"
            disabled={busy}
            onClick={onConfirm}
            className={`inline-flex items-center justify-center gap-2 rounded-ss-sm px-4 py-2 text-sm font-semibold disabled:opacity-70 ${confirmClasses}`}
          >
            {busy ? (
              <>
                <Spinner />
                <span>Working…</span>
              </>
            ) : (
              confirmLabel
            )}
          </button>
        </div>
      </div>
    </div>
  );
}
